/**
 * CSV to JSON Converter for JudgmentPattern
 * Converts CSV format to JSON format for bulk import
 */

export interface JudgmentPatternJson {
  name: string;
  description: string;
  scoreConditions: Record<string, { min: number; max: number }>;
  desc11?: string;
  desc12?: string;
  desc13?: string;
  desc14?: string;
  desc15?: string;
  desc16?: string;
  desc21?: string;
  desc22?: string;
  desc23?: string;
  desc24?: string;
  desc25?: string;
  desc31?: string;
  desc32?: string;
  desc33?: string;
  desc34?: string;
  desc35?: string;
  desc41?: string;
  desc42?: string;
  desc43?: string;
  desc44?: string;
  desc45?: string;
  desc46?: string;
  desc47?: string;
}

export interface ConversionResult {
  success: boolean;
  patterns: JudgmentPatternJson[];
  errors: string[];
  totalRows: number;
  successCount: number;
  errorCount: number;
}

/**
 * Parse CSV content with proper handling of quoted fields
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  
  return result;
}

/**
 * Parse scoreConditions from CSV format to JSON format
 * CSV format: "A": { "min": 0,"max": 0},"B": { "min": 0,"max": 0},...
 * JSON format: { "A": { "min": 0, "max": 0 }, "B": { "min": 0, "max": 0 }, ... }
 */
function parseScoreConditions(csvValue: string): Record<string, { min: number; max: number }> | null {
  try {
    // Remove surrounding quotes if present
    let cleaned = csvValue.trim();
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      cleaned = cleaned.slice(1, -1);
    }
    
    // Replace escaped quotes with regular quotes
    cleaned = cleaned.replace(/""/g, '"');
    
    // Remove any leading/trailing whitespace after quote replacement
    cleaned = cleaned.trim();
    
    // Wrap in braces if not already wrapped
    if (!cleaned.startsWith('{')) {
      cleaned = '{' + cleaned + '}';
    }
    
    // Fix common JSON formatting issues
    // Remove extra spaces around colons and commas
    cleaned = cleaned.replace(/\s*:\s*/g, ':');
    cleaned = cleaned.replace(/\s*,\s*/g, ',');
    // Fix spacing inside objects like { "min" -> {"min"
    cleaned = cleaned.replace(/\{\s+"/g, '{"');
    cleaned = cleaned.replace(/"\s+\}/g, '"}');
    
    // Parse as JSON
    const parsed = JSON.parse(cleaned);
    
    // Validate structure
    for (const [key, value] of Object.entries(parsed)) {
      const condition = value as { min?: number; max?: number };
      if (typeof condition.min !== 'number' || typeof condition.max !== 'number') {
        console.error(`Invalid condition for key ${key}:`, condition);
        return null;
      }
    }
    
    return parsed;
  } catch (e) {
    console.error('Failed to parse scoreConditions:', csvValue);
    console.error('Parse error:', e);
    return null;
  }
}

/**
 * Convert CSV content to JSON array
 */
export function convertCsvToJson(csvContent: string): ConversionResult {
  const lines = csvContent.split('\n').filter(line => line.trim() !== '');
  const errors: string[] = [];
  const patterns: JudgmentPatternJson[] = [];
  
  if (lines.length < 2) {
    return {
      success: false,
      patterns: [],
      errors: ['CSV file must have at least a header row and one data row'],
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
    };
  }
  
  // Parse header
  const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
  
  // Expected headers mapping (CSV column name -> JSON field name)
  const headerMapping: Record<string, string> = {
    'name': 'name',
    'description': 'description',
    'scoreconditions': 'scoreConditions',
    'desc11': 'desc11',
    'desc12': 'desc12',
    'desc13': 'desc13',
    'desc14': 'desc14',
    'desc15': 'desc15',
    'desc16': 'desc16',
    'desc21': 'desc21',
    'desc22': 'desc22',
    'desc23': 'desc23',
    'desc24': 'desc24',
    'desc25': 'desc25',
    'desc31': 'desc31',
    'desc32': 'desc32',
    'desc33': 'desc33',
    'desc34': 'desc34',
    'desc35': 'desc35',
    'desc41': 'desc41',
    'desc42': 'desc42',
    'desc43': 'desc43',
    'desc44': 'desc44',
    'desc45': 'desc45',
    'desc46': 'desc46',
    'desc47': 'desc47',
  };
  
  // Validate required headers
  const requiredHeaders = ['name', 'description', 'scoreconditions'];
  for (const required of requiredHeaders) {
    if (!headers.includes(required)) {
      errors.push(`Missing required header: ${required}`);
    }
  }
  
  if (errors.length > 0) {
    return {
      success: false,
      patterns: [],
      errors,
      totalRows: lines.length - 1,
      successCount: 0,
      errorCount: lines.length - 1,
    };
  }
  
  // Process data rows
  for (let i = 1; i < lines.length; i++) {
    const rowNumber = i + 1;
    const values = parseCSVLine(lines[i]);
    
    if (values.length < headers.length) {
      errors.push(`Row ${rowNumber}: Insufficient columns (expected ${headers.length}, got ${values.length})`);
      continue;
    }
    
    const pattern: Partial<JudgmentPatternJson> = {};
    let rowHasError = false;
    
    for (let j = 0; j < headers.length; j++) {
      const header = headers[j];
      const value = values[j]?.trim() || '';
      const jsonField = headerMapping[header];
      
      if (!jsonField) continue;
      
      if (jsonField === 'scoreConditions') {
        const scoreConditions = parseScoreConditions(value);
        if (!scoreConditions) {
          errors.push(`Row ${rowNumber}: Invalid scoreConditions format`);
          rowHasError = true;
          break;
        }
        pattern.scoreConditions = scoreConditions;
      } else if (jsonField === 'name' || jsonField === 'description') {
        if (!value) {
          errors.push(`Row ${rowNumber}: Missing required field '${jsonField}'`);
          rowHasError = true;
          break;
        }
        (pattern as any)[jsonField] = value;
      } else {
        // Optional desc fields
        if (value) {
          (pattern as any)[jsonField] = value;
        }
      }
    }
    
    if (!rowHasError && pattern.name && pattern.description && pattern.scoreConditions) {
      patterns.push(pattern as JudgmentPatternJson);
    }
  }
  
  return {
    success: errors.length === 0,
    patterns,
    errors,
    totalRows: lines.length - 1,
    successCount: patterns.length,
    errorCount: lines.length - 1 - patterns.length,
  };
}

/**
 * Download JSON data as a file
 */
export function downloadJsonFile(data: JudgmentPatternJson[], filename: string = 'judgment-patterns.json'): void {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
